import { IRequestParams, IResponse, ISimpleResponse } from '@dash/apiInstance';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import { sendRequest } from 'src/helpers/saga';
import ApiInstance from 'src/helpers/apiInstance';
import { executeAction } from '../../../../../../../../../helpers/api';
import { logger } from '../../../../../../../../../helpers/logger';
import { t } from '../../../../../../../../../i18n';
import notifications from '../../../../../../../../../redux/notifications/actions';
import actionTypes from './actionTypes';
import { ConfirmCardAction, ProcessActionRequestAction, setupIntentCardRequestAction } from './actions.d';
import { IProcessActionResponse, ISetupIntentCardResponse } from './types.d';
import cardsActionTypes from '../../../../tabs/cards/actionTypes';
import actions from './actions';

const unitSetupIntentsCardsApi = new ApiInstance('units/:id/setup_intents/cards');
const subunitSetupIntentsCardsApi = new ApiInstance('subunits/:id/setup_intents/cards');

function* processAction({ payload }: ProcessActionRequestAction<{}, {}>) {
  try {
    const { url, method, data, type, id, key, cardAddingStrategy } = payload;

    const result: IResponse<IProcessActionResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      executeAction,
      { url, method, data },
      notifications.REQUEST_ERROR,
    );

    if (result?.messages || result?.success) {
      if (key === 'create_card') {
        yield put({ type: cardsActionTypes.LOAD_CARDS_REQUEST, payload: { type, id, cardAddingStrategy } });
        yield put({ type: actionTypes.ADD_CREDIT_CARD_SUCCESS });
        yield put({ type: actionTypes.CHANGE_MODAL_VISIBILITY });

        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.created_successfully', {
              item: t('dash.clients.subUnit.card_label'),
            }),
          }),
        );
      } else {
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: result?.messages?.join(' ') || '',
          }),
        );
      }
    } else {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );

      if (key === 'create_card') {
        yield put({ type: actionTypes.ADD_CREDIT_CARD_ERROR, payload: { data: result } });
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* confirmCardAdded({ payload }: ConfirmCardAction) {
  const { becsId, clientId, type } = payload;

  try {
    const apiPrefix = type === 'subUnit' ? 'subunits' : 'units';
    const confirmAddApi = new ApiInstance(
      `${apiPrefix}/${clientId}/payment_methods/becs_bank_accounts/${becsId}/added`,
    );

    const result: ISimpleResponse<{}> = yield call(sendRequest, confirmAddApi.post, {}, notifications.REQUEST_ERROR);

    if (result.status === 200) {
      yield put({ type: cardsActionTypes.LOAD_CARDS_REQUEST, payload: { type, id: clientId } });
      yield put({ type: actionTypes.ADD_CREDIT_CARD_SUCCESS });
      yield put({ type: actionTypes.CHANGE_MODAL_VISIBILITY });

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.added_successfully', {
            item: t('dash.units.unit.bankAccounts.bank_account_label'),
          }),
        }),
      );
    } else {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* setupIntentCard({ payload }: setupIntentCardRequestAction) {
  const { id, type } = payload;

  try {
    const api = type === 'subUnit' ? subunitSetupIntentsCardsApi : unitSetupIntentsCardsApi;

    const result: ISimpleResponse<ISetupIntentCardResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      api.post,
      { pathParams: { id } },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200) {
      const { data } = result;

      if (data.client_secret) {
        yield put(actions.setupIntentCardSuccess({ clientSecret: data.client_secret }));
      }
    } else {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([takeEvery(actionTypes.PROCESS_ACTION_REQUEST, processAction)]);
  yield all([takeEvery(actionTypes.SETUP_INTENT_CARD_REQUEST, setupIntentCard)]);
  yield all([takeEvery(actionTypes.CONFIRM_CARD_ADDED_REQUEST, confirmCardAdded)]);
}
